import type IframeProtocol from '@source-digital/iframe-protocol'
import { type IBrokerMessenger } from '../../communication/base'
import type IframeProtocolTarget from '@source-digital/iframe-protocol/dist/types/iframe-protocol-target'
import { type IEventHandler } from '@source-digital/sd-app-core'

export class IFrameMessengerAdapter implements IBrokerMessenger {
  ip: IframeProtocol
  targetMap = new Map<string, IframeProtocolTarget>()
  #offHandlerMap = new Map<string, Map<string, Array<{ handler: IEventHandler, off(): void }>>>()

  constructor (ip: IframeProtocol) {
    this.ip = ip
  }

  #saveEventHandler (eventName: string, handler: IEventHandler, receiverId: string, off: () => void): void {
    if (!this.#offHandlerMap.has(eventName)) {
      this.#offHandlerMap.set(eventName, new Map())
    }
    if (this.#offHandlerMap.get(eventName)?.has(receiverId) !== true) {
      this.#offHandlerMap.get(eventName)?.set(receiverId, [])
    }
    this.#offHandlerMap.get(eventName)?.get(receiverId)?.push({ handler, off })
  }

  emit (eventName: string, data: Transferable, from: string, to?: string): void {
    if (to != null) {
      this.targetMap.get(to)?.emit(eventName, { data, from })
      return
    }
    this.ip.emit(eventName, { data, from })
  }

  on (eventName: string, handler: IEventHandler, receiverId: string): void {
    const off = this.targetMap.get(receiverId)?.on(eventName, handler as () => void)
    if (off != null) this.#saveEventHandler(eventName, handler, receiverId, off)
  }

  once (eventName: string, handler: IEventHandler, receiverId: string): void {
    this.targetMap.get(receiverId)?.once(eventName, handler as () => void)
  }

  off (eventName: string, handler: IEventHandler, receiverId: string): void {
    const handlers = this.#offHandlerMap.get(eventName)
    if (handlers?.has(receiverId) !== true) return
    const found = handlers.get(receiverId)?.find(i => i.handler === handler)
    if (found == null) return
    found.off()
  }

  registerApi (apiName: string, apiDefinition: unknown): void {
    this.ip.registerApi(apiName, apiDefinition)
  }

  unregisterApi (apiName: string): void {
    this.ip.off(apiName)
  }

  hasClient (receiverId: string): boolean {
    return this.targetMap.get(receiverId) != null
  }

  async connectClientApi<T = any> (receiverId: string, apiName: string): Promise<T> {
    const targetIp = this.targetMap.get(receiverId)
    if (targetIp == null) {
      throw new Error(`target ${receiverId} not found`)
    }
    if (!targetIp.ready) await targetIp.wait('ready')
    return await targetIp?.getApi(apiName) as T
  }
}
